// src/pages/Services/customerService.ts
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../Firebase/Config';

const CUSTOMERS_COLLECTION = 'customers';

export interface CustomerProfile {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  photoURL?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

const mapDocToProfile = (uid: string, data: any): CustomerProfile => ({
  uid,
  name: data.name || '',
  email: data.email || '',
  phone: data.phone || undefined,
  photoURL: data.photoURL || undefined,
  isActive: data.isActive !== false,
  createdAt: toDate(data.createdAt),
  updatedAt: toDate(data.updatedAt),
});

export const createCustomerProfile = async (
  uid: string,
  data: { name: string; email: string; phone?: string }
): Promise<void> => {
  await setDoc(doc(db, CUSTOMERS_COLLECTION, uid), {
    name: data.name,
    email: data.email,
    phone: data.phone || null,
    photoURL: null,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const getCustomerProfile = async (uid: string): Promise<CustomerProfile | null> => {
  const snap = await getDoc(doc(db, CUSTOMERS_COLLECTION, uid));
  if (!snap.exists()) return null;
  return mapDocToProfile(uid, snap.data());
};

export const updateCustomerProfile = async (
  uid: string,
  data: Partial<{ name: string; phone: string; photoURL: string }>
): Promise<void> => {
  await updateDoc(doc(db, CUSTOMERS_COLLECTION, uid), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

// ── Profile picture upload ───────────────────────────────────────────────────
// Path: customer_profile_pictures/{uid}/{timestamp}_{sanitisedFileName}
// Best-effort deletes the previous picture (if any) once the new one is live.
export const uploadProfilePicture = async (
  uid: string,
  file: File,
  previousPhotoURL?: string,
  onProgress?: (percent: number) => void
): Promise<string> => {
  const timestamp = Date.now();
  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `customer_profile_pictures/${uid}/${timestamp}_${safeFileName}`;
  const storageRef = ref(storage, storagePath);

  const url = await new Promise<string>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file, { contentType: file.type });
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      (error) => {
        console.error('Profile picture upload error:', error);
        reject(new Error(`Upload failed: ${error.message}`));
      },
      async () => {
        try {
          resolve(await getDownloadURL(uploadTask.snapshot.ref));
        } catch (err) {
          reject(new Error('Failed to get download URL after upload'));
        }
      }
    );
  });

  if (previousPhotoURL) {
    try {
      await deleteObject(ref(storage, previousPhotoURL));
    } catch (error) {
      console.warn('Could not delete previous profile picture:', error);
    }
  }

  return url;
};
